/**
 * 🆕 [Phase 46 BIS] Util centralisé de formatage de timestamps en heure locale Québec.
 *
 * Élimine la classe entière de bugs « affichage UTC au lieu de local » qui apparaissait
 * sur Calendrier, RDV, Congés, Audit logs, etc.
 *
 * Usage :
 *   formatLocalTime("2026-06-15T14:32:00Z")     → "15 juin 2026, 10:32"
 *   formatLocalTime(iso, { withSeconds: true }) → "15 juin 2026, 10:32:00"
 *   formatLocalTime(iso, { dateOnly: true })    → "15 juin 2026"
 *   formatLocalTime(iso, { timeOnly: true })    → "10:32"
 *   formatLocalTime(iso, { format: 'short' })   → "15/06/26 10:32"
 *
 * Cas edge : null/undefined/string invalide → retourne "—" (silencieux, pas de crash).
 *
 * Backend : tous les timestamps doivent inclure le suffixe Z ou un offset (+HH:MM).
 * Une string ISO SANS info de timezone (ex: "2026-06-15T14:32:00") est ambiguë —
 * cette fonction l'interprète comme UTC par défaut (comportement JS standard), mais
 * idéalement on devrait forcer le backend à toujours suffixer.
 */
const TZ = 'America/Toronto'; // Équivalent EST/EDT Québec (Montréal)
const LOCALE = 'fr-CA';
const FALLBACK = '—';

function _parse(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

export function formatLocalTime(iso, options = {}) {
  const {
    withSeconds = false,
    dateOnly = false,
    timeOnly = false,
    format = 'long', // 'long' | 'short'
    weekday = false, // 🆕 [P1 ext] true | 'short' | 'long' — ajoute le jour de la semaine
    withoutYear = false, // 🆕 [P1 SUITE] supprime l'année (utile pour "lundi 15 juin")
  } = options;

  const d = _parse(iso);
  if (!d) return FALLBACK;

  // Normaliser weekday : true → 'long', false → undefined, string → tel quel
  const weekdayOpt = weekday === true ? 'long' : (weekday || undefined);

  try {
    if (timeOnly) {
      return d.toLocaleTimeString(LOCALE, {
        timeZone: TZ,
        hour: '2-digit', minute: '2-digit',
        ...(withSeconds && { second: '2-digit' }),
        hour12: false,
      });
    }
    if (dateOnly) {
      return d.toLocaleDateString(LOCALE, {
        timeZone: TZ,
        ...(weekdayOpt && { weekday: weekdayOpt }),
        ...(!withoutYear && { year: format === 'short' ? '2-digit' : 'numeric' }),
        month: format === 'short' ? '2-digit' : 'long',
        day: '2-digit',
      });
    }
    // Combined
    const dateOpts = {
      timeZone: TZ,
      ...(weekdayOpt && { weekday: weekdayOpt }),
      ...(!withoutYear && { year: format === 'short' ? '2-digit' : 'numeric' }),
      month: format === 'short' ? '2-digit' : 'long',
      day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      ...(withSeconds && { second: '2-digit' }),
      hour12: false,
    };
    return d.toLocaleString(LOCALE, dateOpts).replace(',', '');
  } catch {
    return FALLBACK;
  }
}

export function formatLocalDate(iso) {
  return formatLocalTime(iso, { dateOnly: true });
}

export function formatLocalTimeOnly(iso) {
  return formatLocalTime(iso, { timeOnly: true });
}

/**
 * Retourne true si la string ISO ne contient PAS d'info de timezone (ni Z ni offset).
 * Utile pour flagger les cas ambigus en debug.
 */
export function isAmbiguousIso(iso) {
  if (!iso || typeof iso !== 'string') return false;
  return !/(?:Z|[+-]\d{2}:?\d{2})$/.test(iso.trim());
}

export default formatLocalTime;
